import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';
import type { ArgsFor } from 'ember-modifier';
import type Owner from '@ember/owner';

const PULL_THRESHOLD = 80;
const MAX_PULL = 120;
const INDICATOR_HEIGHT = 64;

interface PullToRefreshSignature {
  Args: {
    Positional: [onRefresh: () => void | Promise<void>];
  };
  Element: HTMLElement;
}

export default class PullToRefreshModifier extends Modifier<PullToRefreshSignature> {
  private indicator: HTMLElement | null = null;
  private startY = 0;
  private currentPull = 0;
  private isRefreshing = false;
  private latestOnRefresh: (() => void | Promise<void>) | null = null;

  private onTouchStart: ((e: TouchEvent) => void) | null = null;
  private onTouchMove: ((e: TouchEvent) => void) | null = null;
  private onTouchEnd: (() => void) | null = null;
  private onTouchCancel: (() => void) | null = null;
  private attachedElement: HTMLElement | null = null;

  constructor(owner: Owner, args: ArgsFor<PullToRefreshSignature>) {
    super(owner, args);
    registerDestructor(this, () => this.teardown());
  }

  modify(element: HTMLElement, [onRefresh]: [() => void | Promise<void>]) {
    this.latestOnRefresh = onRefresh;

    if (this.attachedElement === element) return;

    this.teardown();

    if (!('ontouchstart' in window)) return;

    this.indicator = this.createIndicator();
    element.insertBefore(this.indicator, element.firstChild);

    this.onTouchStart = (e: TouchEvent) => {
      if (this.isRefreshing) return;
      this.startY = e.touches[0]?.clientY ?? 0;
      this.currentPull = 0;
    };

    this.onTouchMove = (e: TouchEvent) => {
      if (this.isRefreshing) return;
      const touch = e.touches[0];
      if (!touch) return;

      const deltaY = touch.clientY - this.startY;
      const scrollTop =
        window.scrollY || document.documentElement.scrollTop || 0;

      if (deltaY > 0 && scrollTop === 0) {
        e.preventDefault();
        this.currentPull = Math.min(deltaY, MAX_PULL);
        this.setIndicatorHeight(this.currentPull, false);
      } else if (this.currentPull > 0) {
        this.currentPull = 0;
        this.setIndicatorHeight(0, true);
      }
    };

    const resetGesture = () => {
      this.isRefreshing = false;
      this.currentPull = 0;
      this.setIndicatorHeight(0, true);
    };

    this.onTouchEnd = () => {
      if (this.isRefreshing) return;

      const pull = this.currentPull;
      this.currentPull = 0;

      if (pull >= PULL_THRESHOLD) {
        this.isRefreshing = true;
        this.setIndicatorHeight(INDICATOR_HEIGHT, false);

        const result = this.latestOnRefresh?.();
        const minDelay = new Promise<void>((resolve) =>
          setTimeout(resolve, 2000)
        );
        const done = () => {
          this.isRefreshing = false;
          this.setIndicatorHeight(0, true);
        };

        void Promise.all([Promise.resolve(result), minDelay]).finally(done);
      } else {
        this.setIndicatorHeight(0, true);
      }
    };

    this.onTouchCancel = resetGesture;

    element.addEventListener('touchstart', this.onTouchStart, {
      passive: true,
    });
    element.addEventListener('touchmove', this.onTouchMove, {
      passive: false,
    });
    element.addEventListener('touchend', this.onTouchEnd, { passive: true });
    element.addEventListener('touchcancel', this.onTouchCancel, {
      passive: true,
    });

    this.attachedElement = element;
  }

  private teardown() {
    if (this.attachedElement) {
      if (this.onTouchStart)
        this.attachedElement.removeEventListener(
          'touchstart',
          this.onTouchStart
        );
      if (this.onTouchMove)
        this.attachedElement.removeEventListener('touchmove', this.onTouchMove);
      if (this.onTouchEnd)
        this.attachedElement.removeEventListener('touchend', this.onTouchEnd);
      if (this.onTouchCancel)
        this.attachedElement.removeEventListener(
          'touchcancel',
          this.onTouchCancel
        );
      this.attachedElement = null;
    }
    this.indicator?.remove();
    this.indicator = null;
    this.onTouchStart = null;
    this.onTouchMove = null;
    this.onTouchEnd = null;
    this.onTouchCancel = null;
  }

  private createIndicator(): HTMLElement {
    const el = document.createElement('div');
    el.style.cssText =
      'display:flex;justify-content:center;align-items:center;height:0;overflow:hidden;';
    const spinner = document.createElement('span');
    spinner.className = 'loading loading-spinner loading-md';
    el.appendChild(spinner);
    return el;
  }

  private setIndicatorHeight(height: number, animate: boolean) {
    if (!this.indicator) return;
    this.indicator.style.transition = animate ? 'height 0.2s ease' : 'none';
    this.indicator.style.height = `${height}px`;
  }
}
